import { Injectable, NgZone, Renderer2, inject } from '@angular/core';
import {
  CLEVER_PUSH_ENVIRONMENT_CONFIG,
  CleverPushConversionEvent,
  CleverPushInfrastructure,
  CleverPushNotification,
  CleverPushNotificationWeb,
  CleverPushPlatform,
  CleverPushService,
  CleverPushSetupConfig,
  CleverPushTopicType,
  FEATURE_STORAGE_KEY,
} from '@zero/feature-clever-push/base';
import { AuthStateFacade } from '@zero/shared/util-global-state';
import { ScriptService } from '@zero/shared/util-services';
import { parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { BehaviorSubject, catchError, firstValueFrom, of } from 'rxjs';
import { CleverPushEvent } from '../entities/clever-push-web.entity';

interface CleverPush {
  push: (args: unknown[]) => void;
  setAuthorizerToken: (token: string) => void;
  on: (event: CleverPushEvent, callback: (err: unknown, data: unknown) => void) => void;
  once: (event: CleverPushEvent, callback: (err: unknown, data: unknown) => void) => void;
}

declare global {
  interface Window {
    CleverPush: CleverPush;
    cleverPushInitCallback: (err: unknown) => void;
  }
}

@Injectable()
export class CleverPushWebService implements CleverPushService {
  readonly #zone = inject(NgZone);
  readonly #scriptService = inject(ScriptService);
  readonly #cleverPushInfrastructure = inject(CleverPushInfrastructure);
  readonly #cleverPushConfig = inject(CLEVER_PUSH_ENVIRONMENT_CONFIG);
  readonly #authFacade = inject(AuthStateFacade);

  #cleverPush: CleverPush | undefined;

  readonly #ready = new BehaviorSubject<boolean>(false);
  public readonly ready$ = this.#ready.asObservable();

  readonly #notifications = new BehaviorSubject<CleverPushNotification<undefined | CleverPushTopicType>[]>([]);
  public readonly notifications$ = this.#notifications.asObservable();

  readonly #hasPermission = new BehaviorSubject<boolean>(false);
  public readonly hasPermission$ = this.#hasPermission.asObservable();

  #renderer: Renderer2;

  public async setup({ renderer }: CleverPushSetupConfig): Promise<void> {
    const isFeatureEnabled = await this.isFeatureEnabled();
    if (!isFeatureEnabled) {
      return;
    }

    this.#renderer = renderer;
    const { channelIdWeb } = this.#cleverPushConfig;

    return this.#zone.runOutsideAngular(async () => {
      await this.#scriptService.injectScript(`https://static.cleverpush.com/channel/loader/${channelIdWeb}.js`, renderer);
      this.#cleverPush = window.CleverPush;
      this.onCleverPushEventOnce(CleverPushEvent.INITIALIZED, () => {
        this.#ready.next(true);
        this.subscribeToEvents();
        this.getNotifications();
        this.hasPermission().then((hasPermission) => this.#hasPermission.next(hasPermission));
      });
    });
  }

  private subscribeToEvents() {
    this.onCleverPushEvent(CleverPushEvent.UNSUBSCRIBED, () => {
      const { channelIdWeb } = this.#cleverPushConfig;
      this.#scriptService.removeScript(`https://static.cleverpush.com/channel/loader/${channelIdWeb}.js`, this.#renderer);
    });
  }

  public requestPermission(): Promise<boolean> {
    return new Promise((resolve) => {
      this.pushCleverPush([
        'triggerOptIn',
        (err: unknown, subscriptionId: string) => {
          if (err) {
            this.#hasPermission.next(false);
            resolve(false);
          } else {
            this.#hasPermission.next(!!subscriptionId);
            resolve(!!subscriptionId);
          }
        },
      ]);
    });
  }

  public async hasPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      return false;
    }

    return Notification.permission === 'granted';
  }

  public subscribe(): Promise<string | null> {
    return new Promise((resolve) => {
      this.pushCleverPush([
        'subscribe',
        (err: unknown, subscriptionId: string) => {
          if (err) {
            resolve(null);
          } else {
            resolve(subscriptionId);
          }
        },
      ]);
    });
  }

  public unsubscribe(): Promise<void> {
    return new Promise((resolve) => {
      this.onCleverPushEventOnce(CleverPushEvent.UNSUBSCRIBED, () => {
        resolve();
      });
      this.pushCleverPush(['unsubscribe']);
    });
  }

  public hasSubscribed(): Promise<boolean> {
    return new Promise((resolve) => {
      this.pushCleverPush([
        'hasSubscribed',
        (hasSubscribed: boolean) => {
          resolve(hasSubscribed);
        },
      ]);
    });
  }

  public async getSubscriptionId(): Promise<string | null> {
    return new Promise((resolve) => {
      if (!this.#cleverPush) {
        return resolve(null);
      }

      this.pushCleverPush([
        'getSubscriptionId',
        (subscriptionId: string | boolean) => {
          resolve(subscriptionId ? subscriptionId.toString() : null);
        },
      ]);
    });
  }

  public async setAuthToken(): Promise<void> {
    if (!this.#cleverPush) {
      return;
    }

    try {
      await firstValueFrom(this.#cleverPushInfrastructure.getOAuthToken().pipe(catchError(() => of(null)))).then(async (response) => {
        if (response) {
          const { token } = response;
          await this.setAuthorizerToken(token);
        }
      });
    } catch (error) {
      console.error('setAuthToken error', error);
    }
  }

  public async processSubscriptionIdAfterLogin(): Promise<void> {
    if (!this.#cleverPush) {
      return;
    }

    try {
      const subscriptionId = await this.getSubscriptionId();
      if (!subscriptionId) {
        return;
      }

      await firstValueFrom(
        this.#cleverPushInfrastructure.postSubscription({
          subscriptionId,
        }),
      );
    } catch (error) {
      console.error(error);
    }
  }

  public async removeSubscriptionIdAfterLogout(): Promise<void> {
    if (!this.#cleverPush) {
      return;
    }

    const isAuthenticated = this.#authFacade.loggedIn;
    if (!isAuthenticated) {
      return;
    }

    try {
      const isAvailable = await this.isFeatureEnabled();
      if (!isAvailable) {
        return;
      }

      const hasPermission = await this.hasPermission();
      if (!hasPermission) {
        return;
      }

      const subscriptionId = await this.getSubscriptionId();
      if (!subscriptionId) {
        return;
      }

      await firstValueFrom(
        this.#cleverPushInfrastructure.deleteSubscriptionById({
          subscriptionId,
        }),
      );
    } catch (error) {
      console.error(error);
    }
  }

  public async authorize(): Promise<void> {
    try {
      await this.setAuthToken();
      await this.subscribe();
      await this.processSubscriptionIdAfterLogin();
    } catch (error) {
      console.error(error);
    }
  }

  public async getTopics(): Promise<string[]> {
    return new Promise((resolve) => {
      this.ready$.subscribe((ready) => {
        if (!ready) {
          return;
        }
        this.pushCleverPush([
          'getTopics',
          (err: unknown, topics: string[]) => {
            if (err) {
              resolve([]);
            }
            resolve(topics);
          },
        ]);
      });
    });
  }

  public async setTopics(topics: string[]): Promise<void> {
    return new Promise((resolve) => {
      this.pushCleverPush(['setTopics', topics]);
      resolve();
    });
  }

  public async getNotifications(): Promise<CleverPushNotification<undefined | CleverPushTopicType>[]> {
    return new Promise((resolve) => {
      this.pushCleverPush([
        'getNotifications',
        (notifications: CleverPushNotificationWeb[]) => {
          const mappedNotifications = notifications ? notifications.map((notification) => this.mapNotification(notification)) : [];
          this.#notifications.next(mappedNotifications);
          resolve(mappedNotifications ?? []);
        },
      ]);
    });
  }

  public async setAuthorizerToken(token: string): Promise<void> {
    if (this.#cleverPush) {
      this.#zone.runOutsideAngular(() => {
        this.#cleverPush.setAuthorizerToken(token);
      });
    }
  }

  public async tagSubscription(tagId: string): Promise<void> {
    return new Promise((resolve) => {
      this.pushCleverPush(['tagSubscription', tagId]);
      resolve();
    });
  }

  public async untagSubscription(tagId: string): Promise<void> {
    return new Promise((resolve) => {
      this.pushCleverPush(['untagSubscription', tagId]);
      resolve();
    });
  }

  public async getAttribute(key: string): Promise<string | null> {
    return new Promise((resolve) => {
      this.pushCleverPush([
        'getAttribute',
        key,
        (value: string | null) => {
          resolve(value);
        },
      ]);
    });
  }

  public async hasAttribute(key: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.pushCleverPush([
        'hasAttribute',
        key,
        (hasAttribute: boolean) => {
          resolve(hasAttribute);
        },
      ]);
    });
  }

  public async isFeatureEnabled(): Promise<boolean> {
    const isEnabled = localStorage.getItem(FEATURE_STORAGE_KEY) ?? undefined;
    return isEnabled === 'true';
  }

  public async enableFeature(): Promise<void> {
    localStorage.setItem(FEATURE_STORAGE_KEY, 'true');
  }

  public async disableFeature(): Promise<void> {
    localStorage.setItem(FEATURE_STORAGE_KEY, 'false');
  }

  public getPlatform(): CleverPushPlatform {
    return this.#cleverPushConfig?.platform ?? 'WEB';
  }

  public async trackEvent(eventName: CleverPushConversionEvent, properties?: Record<string, unknown>): Promise<void> {
    return new Promise((resolve) => {
      this.ready$.subscribe((ready) => {
        if (!ready) {
          return;
        }
        this.pushCleverPush(['trackEvent', eventName, properties ?? {}]);
        return resolve();
      });
    });
  }

  private pushCleverPush(args: unknown[]): void {
    if (this.#cleverPush) {
      this.#zone.runOutsideAngular(() => {
        this.#cleverPush.push(args);
      });
    }
  }

  private onCleverPushEvent(event: CleverPushEvent, callback: (err: unknown, data: unknown) => void) {
    if (this.#cleverPush) {
      this.#zone.runOutsideAngular(() => {
        this.#cleverPush.on(event, callback);
      });
    }
  }

  private onCleverPushEventOnce(event: CleverPushEvent, callback: (err: unknown, data: unknown) => void) {
    if (this.#cleverPush) {
      this.#zone.runOutsideAngular(() => {
        this.#cleverPush.once(event, callback);
      });
    }
  }

  private mapNotification(notification: CleverPushNotificationWeb): CleverPushNotification<undefined | CleverPushTopicType> {
    const { createdAt, customData, _id, text, title } = notification;

    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    const utcDate = parseISO(createdAt);
    const ts = toZonedTime(utcDate, timeZone);

    return {
      createdAt: ts,
      customData,
      id: _id,
      text,
      title,
      url: null,
    };
  }
}
